import { Stack } from "@mui/material";
import { useMemo } from "react";

import { FeatureCreateOptionDraft } from "../../hooks/useDraftSelectedOption";
import { SpeciesAutocomplete } from "../ObservationSection/components/SpeciesAutocomplete";
import { FeatureCreateOptionEcologicalFunction } from "../types";
import { EcologicalFunctionTypeTree } from "./components/EcologicalFunctionTypeTree";
import { MapEditorFeatureCreateOptionsSelect_EcologicalFunctionFragment } from "./fragments.generated";
import { buildDataMaps } from "./utils";

type Props = {
    researchTypes: MapEditorFeatureCreateOptionsSelect_EcologicalFunctionFragment[];
    draftSelectedOption: Partial<FeatureCreateOptionEcologicalFunction>;
    onUpdate: (option: FeatureCreateOptionDraft) => void;
};

export function EcologicalFunctionSection({ researchTypes, draftSelectedOption, onUpdate }: Props) {
    const { allSpecies, ecologicalFunctionTypeMap } = useMemo(() => buildDataMaps(researchTypes), [researchTypes]);

    const selectedSpecies = draftSelectedOption.species ?? null;

    const filteredResearchTypes = useMemo(
        () =>
            selectedSpecies
                ? researchTypes.filter(
                      rt => rt.speciesV2.some(s => s.species.id === selectedSpecies.id) && rt.ecologicalFunctionTypes.length > 0,
                  )
                : [],
        [researchTypes, selectedSpecies],
    );

    const selectedEcologicalFunctionTypeId = draftSelectedOption.ecologicalFunctionType?.id ?? null;

    return (
        <Stack rowGap={2}>
            <SpeciesAutocomplete
                species={allSpecies}
                selectedSpecies={selectedSpecies}
                onSpeciesChange={species =>
                    onUpdate({
                        type: "ecologicalFunction",
                        species: species ?? undefined,
                    })
                }
            />
            {selectedSpecies && (
                <EcologicalFunctionTypeTree
                    researchTypes={filteredResearchTypes}
                    selectedEcologicalFunctionTypeId={selectedEcologicalFunctionTypeId}
                    onSelectionChange={itemId => {
                        if (!itemId || !selectedSpecies) return;

                        const data = ecologicalFunctionTypeMap.get(itemId);
                        if (!data) return;

                        const { ecologicalFunctionType, researchType } = data;

                        onUpdate({
                            type: "ecologicalFunction",
                            species: selectedSpecies,
                            researchType: { id: researchType.id, name: researchType.name },
                            ecologicalFunctionType,
                        });
                    }}
                />
            )}
        </Stack>
    );
}
